// @mui
import { Box, Card, Grid, Skeleton, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';

// ----------------------------------------------------------------------

export default function OrganizationDetailsFormSkeleton() {
  const theme = useTheme();

  return (
    <Grid
      container
      spacing={3}
      sx={{ px: { xs: 2, sm: 2, md: 2, lg: 0, xl: 0 } }}
    >
      {/* Left Column - Info Card */}
      <Grid item xs={12} md={12} lg={4} xl={4}>
        <Card
          sx={{
            pt: 10,
            pb: 3,
            display: 'flex',
            flex: 1,
            boxShadow: theme.customShadows.z24,
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* Header Section */}
          <Stack
            sx={{
              gap: 5,
              px: 3,
              display: 'flex',
              flex: 1,
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
            }}
          >
            {/* Logo/Avatar Skeleton */}
            <Skeleton
              variant="circular"
              width={144}
              height={144}
              sx={{ borderRadius: 200 }}
            />
            {/* Organization Name */}
            <Skeleton variant="rounded" width="80%" height={32} />
            {/* Status Chip */}
            <Skeleton
              variant="rounded"
              width={80}
              height={24}
              sx={{ borderRadius: '12px' }}
            />
          </Stack>

          {/* Divider */}
          <Box
            sx={{ width: '100%', height: 1, bgcolor: 'divider', opacity: 0.3 }}
          />

          {/* Details Section */}
          <Stack sx={{ gap: 2, flex: 1, px: 3 }}>
            {/* NIP */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="70%" height={20} />
            </Stack>
            {/* City */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="60%" height={20} />
            </Stack>
            {/* Address */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="80%" height={20} />
            </Stack>
            {/* Zip Code */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="50%" height={20} />
            </Stack>
            {/* Working Hours */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="65%" height={20} />
            </Stack>
            {/* Phone */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="55%" height={20} />
            </Stack>
            {/* Website */}
            <Stack sx={{ gap: 2, flexDirection: 'row', alignItems: 'center' }}>
              <Skeleton variant="circular" width={16} height={16} />
              <Skeleton variant="rounded" width="75%" height={20} />
            </Stack>
          </Stack>

          {/* Divider */}
          <Box
            sx={{ width: '100%', height: 1, bgcolor: 'divider', opacity: 0.3 }}
          />

          {/* Specializations Section */}
          <Stack sx={{ gap: 2, px: 2 }}>
            <Skeleton variant="rounded" width={100} height={20} />
            <Stack sx={{ gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}>
              {[60, 80, 70, 90, 65, 85, 75, 95].map((width, index) => (
                <Skeleton
                  key={index}
                  variant="rounded"
                  width={width}
                  height={24}
                  sx={{ borderRadius: '12px' }}
                />
              ))}
            </Stack>
          </Stack>
        </Card>
      </Grid>

      {/* Right Column - Form */}
      <Grid item xs={12} md={12} lg={8} xl={8}>
        <Card
          sx={{
            p: { xs: 2, sm: 2, md: 3, lg: 3, xl: 3 },
            display: 'flex',
            flex: 1,
            boxShadow: theme.customShadows.z24,
          }}
        >
          <Stack sx={{ height: 1, flex: 1 }}>
            <Stack
              sx={{
                pb: 3,
                gap: 3,
                position: 'relative',
              }}
            >
              {/* Form Fields */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                  gap: { xs: 3, md: 2 },
                }}
              >
                {/* Name and NIP */}
                <Box
                  sx={{
                    display: 'grid',
                    gridColumn: { xs: '1', md: '1 / span 2' },
                    gridTemplateColumns: { xs: '1fr', md: '4fr 2fr' },
                    gap: { xs: 3, md: 2 },
                  }}
                >
                  <Skeleton variant="rounded" height={56} />
                  <Skeleton variant="rounded" height={56} />
                </Box>

                {/* Address Fields */}
                <Box
                  sx={{
                    display: 'grid',
                    gridColumn: { xs: '1', md: '1 / span 2' },
                    gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                    gap: { xs: 3, md: 2 },
                  }}
                >
                  <Skeleton variant="rounded" height={56} />
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                      gap: { xs: 3, md: 2 },
                    }}
                  >
                    <Skeleton variant="rounded" height={56} />
                    <Skeleton variant="rounded" height={56} />
                  </Box>
                </Box>

                {/* Phone Number */}
                <Skeleton variant="rounded" height={56} />

                {/* Website */}
                <Skeleton variant="rounded" height={56} />

                {/* Specializations */}
                <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                  <Skeleton variant="rounded" height={56} />
                </Box>

                {/* Description */}
                <Box sx={{ gridColumn: { xs: '1', md: '1 / span 2' } }}>
                  <Skeleton variant="rounded" height={500} />
                </Box>
              </Box>
            </Stack>

            {/* Button Area */}
            <Stack
              sx={{
                flex: 1,
                flexDirection: 'row',
                justifyContent: 'space-between',
              }}
            >
              <Stack sx={{ flexDirection: 'row', gap: 2 }}>
                {/* Delete Button Skeleton */}
                <Skeleton variant="rounded" width={100} height={40} />
                {/* Activate/Deactivate Button Skeleton */}
                <Skeleton variant="rounded" width={140} height={40} />
              </Stack>
              <Stack>
                <Skeleton variant="rounded" width={120} height={40} />
              </Stack>
            </Stack>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
